import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import {
  Button,
  ButtonGroup,
  Chip,
  FormControlLabel,
  FormGroup,
  MenuItem,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import MDropdownMenuButton from 'common/MDropdownMenuButton';
import { useUserRolePermissions } from 'hooks';
import usePersistentState from 'hooks/usePersistentState';
import { openConfirmationDialog } from 'store/Confirmationdialogslice';
import { BulkActionFooter, PersistentServerSideDataGrid } from 'common';
import { createSnack } from 'snackbar/snackbarSlice';
import { formatNumber } from 'utils/helpers/format';
import { DrawerContext } from 'common/drawer/DrawerContext';
import { loadingManagementApi } from 'feature/loading-management/loadingManagementApi';
import { AuthContext } from 'authentication/AuthContext';
import { EvaluateFlag } from 'common/FeatureFlags/Flag';
import { endDateDisplayUtils } from './endDateDisplayUtils';
import { openUnsavedEditSupplierAuthDialog } from '../store/supplierAuthorizationSlice';
import {
  useBulkActivateSupplierAuthorizationsMutation,
  useBulkDeactivateSupplierAuthorizationsMutation,
  useGetSupplierAuthorizationsListQuery,
  useLazyGetExportListQuery,
  useToggleSupplierAuthorizationMutation,
  useUpdateTwoPartyExchangeStatusMutation,
} from './supplierAuthorizationsEndpoints';
import EditPanel from '../edit-panel/EditPanel.connector';

const INACTIVE_TOOLTIP_MESSAGE = 'Authorization must be active to enable actions.';
const TWO_PARTY_LABELS = {
  pending: `Two Party Exchange Pending\nSupplier Approval`,
  turn_off: (
    <>
      <strong>Turn Off</strong> Two Party Exchange
    </>
  ),
  turn_on: (
    <>
      <strong>Turn On</strong> Two Party Exchange
    </>
  ),
};

const SORT_COLUMN_MAP = {
  productId: 'product',
};

const formatGridNumber = ({ value }) => {
  if (value === null || value === undefined || value === '') return '';
  return formatNumber(value, { defaultValue: 0 });
};

const getTwoPartyExchangeState = exchange => {
  if (exchange === null || exchange === undefined) return 'turn_on';
  if (exchange.confirmed === false) return 'pending';
  if (exchange.confirmed === true) return 'turn_off';
  return 'turn_on';
};

const getActiveStatusFilter = statusFilter => {
  const statusValue = String(statusFilter?.value || '').toLowerCase();

  switch (statusValue) {
    case 'allon':
      return true;
    case 'alloff':
      return false;
    default:
      return null;
  }
};

const getMultiSelectValues = filterItems => {
  if (!Array.isArray(filterItems) || filterItems.length === 0) {
    return null;
  }

  return filterItems.map(({ value }) => value).filter(value => !R.isNil(value));
};

const getSupplierIdName = supplier =>
  supplier?.companyCustomerIdName ??
  supplier?.idName ??
  supplier?.customerIdName ??
  supplier?.value;

const getSupplierFilterValues = supplierFilters => {
  if (!Array.isArray(supplierFilters) || supplierFilters.length === 0) {
    return null;
  }

  return supplierFilters
    .map(supplier => ({
      companyCustomerIdName: getSupplierIdName(supplier),
      dataCompanyId: supplier?.dataCompanyId,
    }))
    .filter(supplier => supplier.companyCustomerIdName && !R.isNil(supplier.dataCompanyId));
};

const buildCompanyReference = entity => {
  const companyCustomerIdName = getSupplierIdName(entity);
  const dataCompanyId = entity?.dataCompanyId;

  if (!companyCustomerIdName || R.isNil(dataCompanyId)) {
    return null;
  }

  return {
    companyCustomerIdName,
    dataCompanyId,
  };
};

const buildSupplierReference = (supplierFilters, label) => {
  return supplierFilters.filter(s => s.label === label)[0] || null;
};

const isAbortError = error => {
  const errorStatus = error?.status;
  const errorMessage = String(error?.error || '');

  return errorStatus === 'FETCH_ERROR' && errorMessage.includes('AbortError');
};

const downloadBlob = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(url);
};

const SupplierGridView = ({
  appliedFilters = {},
  supplierOptions = [],
  onExportingChange = null,
}) => {
  const dispatch = useDispatch();
  const { setDrawerComponentProps } = useContext(DrawerContext);
  const { currentAffiliate } = useContext(AuthContext);
  const supplierExportFlag = EvaluateFlag(process.env.REACT_APP_LD_SUPPLIER_AUTH_EXPORT);
  // eslint-disable-next-line no-unused-vars
  const [isProcessing, setIsProcessing] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    if (onExportingChange) {
      onExportingChange(isExporting);
    }
  }, [isExporting, onExportingChange]);

  const [persistedPageSize, setPersistedPageSize] = usePersistentState(
    'supplierAuthorizationsGridPageSize',
    50
  );
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: Number.isFinite(Number(persistedPageSize)) ? Number(persistedPageSize) : 50,
  });
  const [sortModel, setSortModel] = useState([{ field: 'supplier', sort: 'asc' }]);

  const currentSort = sortModel[0] || { field: 'supplier', sort: 'asc' };

  useEffect(() => {
    setPersistedPageSize(paginationModel.pageSize);
  }, [paginationModel.pageSize, setPersistedPageSize]);

  const queryParams = useMemo(() => {
    const sortColumn = SORT_COLUMN_MAP[currentSort.field] || currentSort.field || 'supplier';
    const sortDirection = currentSort.sort === 'asc' ? 'Ascending' : 'Descending';
    const activeStatusFilter = getActiveStatusFilter(appliedFilters.status);
    const affiliate = appliedFilters?.affiliate;
    const supplierId = getSupplierFilterValues(appliedFilters.supplier);
    const locationIds = getMultiSelectValues(appliedFilters.location);
    const productIds = getMultiSelectValues(appliedFilters.product);

    return {
      Page: paginationModel.page,
      Results: paginationModel.pageSize,
      SortColumn: sortColumn,
      SortDirection: sortDirection,
      // Add applied filters to query params (only if they have values)
      ...(affiliate?.customerIdName && !R.isNil(affiliate?.dataCompanyId)
        ? {
            inventoryOwner: {
              companyCustomerIdName: affiliate.customerIdName,
              dataCompanyId: affiliate.dataCompanyId,
            },
          }
        : {}),
      ...(supplierId?.length && { Suppliers: supplierId }),
      ...(typeof activeStatusFilter === 'boolean' && { active: activeStatusFilter }),
      ...(locationIds?.length && { locationIds }),
      ...(productIds?.length && { productIds }),
      // Add search text if present
      ...(appliedFilters.search &&
        appliedFilters.search.trim() && { quickFilter: appliedFilters.search.trim() }),
    };
  }, [
    currentSort.field,
    currentSort.sort,
    paginationModel.page,
    paginationModel.pageSize,
    appliedFilters,
  ]);

  const queryParamsRef = useRef(queryParams);
  useEffect(() => {
    queryParamsRef.current = queryParams;
  }, [queryParams]);

  const {
    data: supplierAuthData,
    error: supplierAuthError,
    isFetching: supplierAuthorizationsPending,
  } = useGetSupplierAuthorizationsListQuery(queryParams, {
    refetchOnMountOrArgChange: true,
  });
  const [toggleSupplierAuthorization] = useToggleSupplierAuthorizationMutation();
  const [bulkDeactivateSupplierAuthorizations] = useBulkDeactivateSupplierAuthorizationsMutation();
  const [bulkActivateSupplierAuthorizations] = useBulkActivateSupplierAuthorizationsMutation();
  const [triggerExportList] = useLazyGetExportListQuery();

  const [updateTwoPartyExchangeStatus] = useUpdateTwoPartyExchangeStatusMutation();
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const prevFilterKeyRef = useRef(JSON.stringify(appliedFilters));

  // Cache to store all rows that have been viewed across pages
  const rowCacheRef = useRef(new Map());

  const handleEdit = useCallback(
    row => {
      setDrawerComponentProps({
        Component: props => (
          <EditPanel {...props} productAuthorizationId={row.productAuthorizationId} />
        ),
        forceCloseSelector: R.path([
          'loadingManagement',
          'supplierAuthorizations',
          'forceCloseEditDrawer',
        ]),
        holdOpenSelector: R.path([
          'loadingManagement',
          'supplierAuthorizations',
          'hasUnsavedEditChanges',
        ]),
        setCurrentDialogOpen: openUnsavedEditSupplierAuthDialog,
      });
    },
    [setDrawerComponentProps]
  );

  const handleTwoPartyMenuClick = useCallback(
    row => {
      const exchangeConfirmed = row.exchange?.confirmed;
      const isTurningOff = exchangeConfirmed === true;
      const nextAction = isTurningOff ? 'off' : 'on';

      dispatch(
        openConfirmationDialog({
          confirmAction: async () => {
            try {
              await updateTwoPartyExchangeStatus({
                isActive: isTurningOff,
                productAuthorizationId: row.productAuthorizationId,
              }).unwrap();

              dispatch(
                createSnack({
                  message: `Two Party Exchange has been successfully turned ${nextAction} for the authorization.`,
                  options: { variant: 'success' },
                  title: 'Action Completed',
                })
              );
            } catch (error) {
              const status = error?.status;
              const predefinedErrors = {
                403: 'User is not associated with the Inventory Owner.',
                404: 'Product Authorization was not found.',
                409: 'An active exchange already exists for this authorization.',
                500: `Due to an error, Two Party Exchange could not be turned ${nextAction} for the authorization.`,
              };
              const errorMessage =
                predefinedErrors[status] ||
                `Due to ${
                  error?.data?.title || error?.error || 'an unexpected error'
                }, Two Party Exchange could not be turned ${nextAction} for the authorization.`;

              dispatch(
                createSnack({
                  message: errorMessage,
                  options: { variant: 'error' },
                  title: 'Action Not Completed',
                })
              );
            }
          },
          header: `Turn ${isTurningOff ? 'Off' : 'On'} Two Party Exchange?`,
          message: `Turn ${nextAction} Two Party Exchange for this authorization? Two Party Exchange can be turned ${
            isTurningOff ? 'on' : 'off'
          } at any time.`,
          params: {},
        })
      );
    },
    [dispatch, updateTwoPartyExchangeStatus]
  );

  useEffect(() => {
    if (!supplierAuthError) return;
    if (isAbortError(supplierAuthError)) return;

    const errorMessage = supplierAuthError?.data?.detail || supplierAuthError?.data?.title;
    dispatch(
      createSnack({
        message: errorMessage,
        options: { variant: 'error' },
        title: 'Action Not Completed',
      })
    );
  }, [dispatch, supplierAuthError]);

  const { isProductLoadingManage } = useUserRolePermissions();

  const currentRows = useMemo(
    () => supplierAuthData?.supplierAuthorizations || [],
    [supplierAuthData?.supplierAuthorizations]
  );

  // Clear cache and selections when filters change
  useEffect(() => {
    const currentKey = JSON.stringify(appliedFilters);
    if (currentKey !== prevFilterKeyRef.current) {
      rowCacheRef.current.clear();
      setRowSelectionModel([]);
      prevFilterKeyRef.current = currentKey;
    }
  }, [appliedFilters]);

  // Update cache whenever new rows are loaded
  useEffect(() => {
    if (currentRows.length > 0) {
      currentRows.forEach(row => {
        rowCacheRef.current.set(row.productAuthorizationId, row);
      });
    }
  }, [currentRows]);

  // Function to get specific rows by IDs from cache
  const getRowsByIds = useCallback(ids => {
    return ids.map(id => rowCacheRef.current.get(id)).filter(row => row !== undefined);
  }, []);

  const currentPageRowIds = useMemo(
    () => currentRows.map(row => row.productAuthorizationId),
    [currentRows]
  );

  const handleRowSelectionModelChange = useCallback(
    newSelectionModel => {
      const currentPageIdSet = new Set(currentPageRowIds);
      const otherPageSelections = rowSelectionModel.filter(id => !currentPageIdSet.has(id));
      const currentPageSelections = newSelectionModel.filter(id => currentPageIdSet.has(id));
      setRowSelectionModel([...otherPageSelections, ...currentPageSelections]);
    },
    [rowSelectionModel, currentPageRowIds]
  );

  const selectedCount = rowSelectionModel.length;
  const hasSelections = selectedCount > 0;

  const currentPageSelection = useMemo(
    () => rowSelectionModel.filter(id => currentPageRowIds.includes(id)),
    [rowSelectionModel, currentPageRowIds]
  );

  const volumeAggregationModel = useMemo(
    () => ({
      authorizationVolume: 'sum',
      remainingVolume: 'sum',
      renewalVolume: 'sum',
    }),
    []
  );

  const confirmToggleSupplierAuthActivation = useCallback(
    async row => {
      try {
        await toggleSupplierAuthorization({
          body: {
            productAuthorizationId: row.productAuthorizationId,
          },
          isActive: row.active,
        }).unwrap();

        dispatch(
          createSnack({
            message: `Authorization has been successfully turned ${row.active ? 'off' : 'on'}.`,
            options: { variant: 'success' },
            title: 'Action Completed',
          })
        );
      } catch (error) {
        const errorMessage =
          error?.data?.detail ||
          `Due to an error, the authorization could not be turned ${row.active ? 'off' : 'on'}.`;
        dispatch(
          createSnack({
            message: errorMessage,
            options: { variant: 'error' },
            title: 'Action Not Completed',
          })
        );
      }
    },
    [dispatch, toggleSupplierAuthorization]
  );

  const handleActivationSwitchChange = useCallback(
    row => {
      const { active, location, productId, productAuthorizationId, supplier } = row;

      // Optimistically update the cache immediately for instant UI feedback
      const patchResult = dispatch(
        loadingManagementApi.util.updateQueryData(
          'getSupplierAuthorizationsList',
          queryParamsRef.current,
          draft => {
            const rowToUpdate = draft.supplierAuthorizations?.find(
              auth => auth.productAuthorizationId === productAuthorizationId
            );
            if (rowToUpdate) {
              rowToUpdate.active = !active;
            }
          }
        )
      );

      dispatch(
        openConfirmationDialog({
          cancelAction: () => {
            // Revert the optimistic update if user cancels
            patchResult.undo();
          },
          confirmAction: () => confirmToggleSupplierAuthActivation(row),
          header: `Turn ${active ? 'Off' : 'On'} Supplier Authorization?`,
          message: `Turn ${
            active ? 'off' : 'on'
          } ${productId} loading for ${supplier} at ${location}? Supplier Authorizations can be turned back ${
            active ? 'on' : 'off'
          } at any time.`,
          params: {
            productAuthorizationId,
          },
        })
      );
    },
    [dispatch, confirmToggleSupplierAuthActivation]
  );

  const columns = useMemo(
    () => [
      {
        field: 'active',
        flex: 0.7,
        headerName: 'Status',
        maxWidth: 96,
        minWidth: 96,
        renderCell: ({ row }) => {
          if (typeof row?.active !== 'boolean') return null;

          return (
            <FormGroup>
              <FormControlLabel
                control={
                  <Switch
                    checked={row.active}
                    disabled={!isProductLoadingManage}
                    onChange={() => handleActivationSwitchChange(row)}
                    size="medium"
                  />
                }
                label={row.active ? 'On' : 'Off'}
                labelPlacement="start"
                sx={{
                  justifyContent: 'center',
                  m: 0,
                  width: '100%',
                }}
              />
            </FormGroup>
          );
        },
      },
      { field: 'supplier', flex: 1, headerName: 'Supplier', minWidth: 180 },
      { field: 'location', flex: 1, headerName: 'Location', minWidth: 160 },
      { field: 'pidxCode', flex: 1, headerName: 'PIDX Code', minWidth: 120 },
      { field: 'productId', flex: 1, headerName: 'Product ID', minWidth: 90 },
      { field: 'productLongName', flex: 1, headerName: 'Product Name', minWidth: 180 },
      { field: 'type', flex: 0.7, headerName: 'Type', minWidth: 96 },
      { field: 'authorizationNumber', flex: 0.5, headerName: 'Auth #', minWidth: 64 },
      {
        field: 'authorizationVolume',
        flex: 1,
        headerName: 'Auth. Vol. (Gal)',
        minWidth: 120,
        type: 'number',
        valueFormatter: formatGridNumber,
      },
      {
        field: 'remainingVolume',
        flex: 1,
        headerName: 'Remaining Vol. (Gal)',
        minWidth: 168,
        type: 'number',
        valueFormatter: formatGridNumber,
      },
      {
        field: 'renewalVolume',
        flex: 1,
        headerName: 'Renewal Vol. (Gal)',
        minWidth: 168,
        type: 'number',
        valueFormatter: formatGridNumber,
      },
      {
        field: 'endDate',
        flex: 1,
        headerName: 'End Date',
        minWidth: 130,
        renderCell: ({ row }) => {
          const { chipBackgroundColor, label, showChip, textColor, tooltip } = endDateDisplayUtils(
            row.endDate
          );

          if (!showChip) {
            return <span>{label}</span>;
          }

          return (
            <Tooltip placement="top-start" title={tooltip}>
              <Chip
                label={label}
                size="small"
                sx={{
                  backgroundColor: chipBackgroundColor,
                  color: textColor,
                  fontFamily: 'Overpass',
                  fontSize: '0.8125rem',
                  fontWeight: 400,
                }}
              />
            </Tooltip>
          );
        },
      },
      ...(isProductLoadingManage
        ? [
            {
              cellClassName: 'css-heap-redact',
              field: 'action',
              flex: 1,
              headerName: 'Actions',
              minWidth: 150,
              renderCell: ({ row }) => {
                if (typeof row?.active !== 'boolean') return null;

                const isSelfOrBlend = row.type === 'SELF' || row.type === 'BLEND';
                if (isSelfOrBlend) return null;

                const isInactive = !row.active;

                const twoPartyState = getTwoPartyExchangeState(row.exchange);
                const twoPartyLabel = (
                  <Typography sx={{ whiteSpace: 'pre-line' }}>
                    {TWO_PARTY_LABELS[twoPartyState]}
                  </Typography>
                );

                const isPending = twoPartyState === 'pending';

                return (
                  <Tooltip placement="top" title={isInactive ? INACTIVE_TOOLTIP_MESSAGE : ''}>
                    <span>
                      <ButtonGroup
                        disabled={isInactive}
                        orientation="horizontal"
                        sx={{ width: '150px' }}
                        variant="outlined"
                      >
                        <Button onClick={() => handleEdit(row)}>Edit</Button>
                        <MDropdownMenuButton>
                          <MenuItem
                            disabled={isPending}
                            onClick={() => !isPending && handleTwoPartyMenuClick(row)}
                          >
                            {twoPartyLabel}
                          </MenuItem>
                        </MDropdownMenuButton>
                      </ButtonGroup>
                    </span>
                  </Tooltip>
                );
              },
              sortable: false,
            },
          ]
        : []),
    ],
    [isProductLoadingManage, handleActivationSwitchChange, handleEdit, handleTwoPartyMenuClick]
  );

  const totalAuthorizations = supplierAuthData?.totalCount ?? 0;
  const authorizationCountLabel = useMemo(() => {
    const totalDisplay = formatNumber(totalAuthorizations, { defaultValue: 0 });

    if (hasSelections) {
      return `${formatNumber(selectedCount, {
        defaultValue: 0,
      })} of ${totalDisplay} Authorizations`;
    }

    return `${totalDisplay} Authorizations`;
  }, [hasSelections, selectedCount, totalAuthorizations]);

  const handleSortModelChange = model => {
    const nextSortModel =
      model && model.length ? [model[model.length - 1]] : [{ field: 'supplier', sort: 'asc' }];
    setSortModel(nextSortModel);
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  };

  const divideSelectedRowsBySupplierKey = useCallback(() => {
    const supplierKeyFn = row =>
      `${row.supplierData?.companyCustomerIdName}-${row.supplierData?.dataCompanyId}`;

    // Get all selected rows from cache (supports multi-page selection)
    const selectedRows = getRowsByIds(rowSelectionModel).map(row => ({
      ...row,
      supplierData: buildSupplierReference(supplierOptions, row.supplier),
    }));

    return R.groupBy(supplierKeyFn, selectedRows);
  }, [getRowsByIds, rowSelectionModel, supplierOptions]);

  const runBulkActionForGroupedRows = useCallback(
    async (groupedRows, actionFn) => {
      const inventoryOwner =
        buildCompanyReference(appliedFilters?.affiliate) || buildCompanyReference(currentAffiliate);

      // Validate before processing
      if (!inventoryOwner) {
        dispatch(
          createSnack({
            message: 'Inventory Owner is required to process the selected authorizations.',
            options: { persist: true, variant: 'error' },
            title: 'Action Not Completed',
          })
        );
        return;
      }

      const actionPromises = Object.values(groupedRows).map(rows => {
        const { supplierData } = rows[0];

        if (!supplierData) {
          return Promise.reject(new Error('Missing supplier data'));
        }

        const authorizationIds = rows.map(row => row.productAuthorizationId);

        return actionFn({
          authorizationIds,
          inventoryOwner,
          supplier: {
            companyCustomerIdName: supplierData.companyCustomerIdName,
            dataCompanyId: supplierData.dataCompanyId,
          },
        });
      });

      await Promise.all(actionPromises);
    },
    [appliedFilters, currentAffiliate, dispatch]
  );

  const handleBulkActivationClick = useCallback(() => {
    const inventoryOwner =
      buildCompanyReference(appliedFilters?.affiliate) || buildCompanyReference(currentAffiliate);
    const supplier = buildCompanyReference(currentAffiliate);

    if (!inventoryOwner || !supplier) {
      dispatch(
        createSnack({
          message:
            'Supplier and Inventory Owner are required to process the selected authorizations.',
          options: { persist: true, variant: 'error' },
          title: 'Action Not Completed',
        })
      );
      return;
    }

    dispatch(
      openConfirmationDialog({
        confirmAction: async () => {
          try {
            await runBulkActionForGroupedRows(divideSelectedRowsBySupplierKey(), async group => {
              await bulkActivateSupplierAuthorizations(group).unwrap();
            });
            dispatch(
              createSnack({
                message: `Authorizations have been successfully turned on.`,
                options: { autoHideDuration: 4000, variant: 'success' },
                title: 'Action Completed',
              })
            );
          } catch {
            dispatch(
              createSnack({
                message: `Due to an error, the authorizations could not be turned on.`,
                options: { persist: true, variant: 'error' },
                title: 'Action Not Completed',
              })
            );
          } finally {
            setRowSelectionModel([]);
          }
        },
        header: `Turn On ${selectedCount} Authorizations?`,
        message: `Are you sure you want to activate the selected authorizations? This action can be undone at any time.`,
      })
    );
  }, [
    appliedFilters?.affiliate,
    bulkActivateSupplierAuthorizations,
    currentAffiliate,
    dispatch,
    selectedCount,
    runBulkActionForGroupedRows,
    divideSelectedRowsBySupplierKey,
  ]);

  const handleBulkDeactivationClick = useCallback(() => {
    const inventoryOwner =
      buildCompanyReference(appliedFilters?.affiliate) || buildCompanyReference(currentAffiliate);
    // this is using affiliare ans should at the very least be using the supplier filter
    const supplier = buildCompanyReference(currentAffiliate);

    if (!inventoryOwner || !supplier) {
      dispatch(
        createSnack({
          message:
            'Supplier and Inventory Owner are required to process the selected authorizations.',
          options: { persist: true, variant: 'error' },
          title: 'Action Not Completed',
        })
      );
      return;
    }

    dispatch(
      openConfirmationDialog({
        confirmAction: async () => {
          try {
            await runBulkActionForGroupedRows(divideSelectedRowsBySupplierKey(), async group => {
              await bulkDeactivateSupplierAuthorizations(group).unwrap();
            });
            dispatch(
              createSnack({
                message: `Authorizations have been successfully turned off.`,
                options: { autoHideDuration: 4000, variant: 'success' },
                title: 'Action Completed',
              })
            );
          } catch (error) {
            dispatch(
              createSnack({
                message: `Due to an error, the authorizations could not be turned off.`,
                options: { persist: true, variant: 'error' },
                title: 'Action Not Completed',
              })
            );
          } finally {
            setRowSelectionModel([]);
          }
        },
        header: `Turn Off ${selectedCount} Authorizations?`,
        message: `Are you sure you want to deactivate the selected authorizations? This action can be undone at any time.`,
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    appliedFilters?.affiliate,
    currentAffiliate,
    dispatch,
    selectedCount,
    bulkDeactivateSupplierAuthorizations,
    rowSelectionModel,
    divideSelectedRowsBySupplierKey,
  ]);

  const CustomActionFooter = useCallback(() => {
    return (
      <BulkActionFooter
        actions={[
          <MenuItem onClick={() => handleBulkActivationClick()}>Turn on all selected</MenuItem>,
          <MenuItem onClick={() => handleBulkDeactivationClick()}>Turn off all selected</MenuItem>,
        ]}
        disabled={!hasSelections}
      />
    );
  }, [handleBulkActivationClick, handleBulkDeactivationClick, hasSelections]);

  const handleExportAllRows = useCallback(async () => {
    setIsExporting(true);
    try {
      const { Page, Results, ...exportQueryParams } = queryParams;

      const result = await triggerExportList(exportQueryParams, false);

      if (result?.error) {
        throw result.error;
      }

      const csvBlob =
        result?.data instanceof Blob
          ? result.data
          : new Blob([result?.data || ''], { type: 'text/csv;charset=utf-8;' });
      const filename = 'supplier-authorizations.csv';

      downloadBlob(csvBlob, filename);

      dispatch(
        createSnack({
          message: 'Export has been successfully downloaded.',
          options: { variant: 'success' },
          title: 'Action Completed',
        })
      );
    } catch (error) {
      const errorMessage =
        error?.data?.detail ||
        error?.data?.title ||
        error?.error ||
        'Due to an error, your supplier authorizations could not be exported to a .CSV file.';

      dispatch(
        createSnack({
          message: errorMessage,
          options: { persist: true, variant: 'error' },
          title: 'Action Not Completed',
        })
      );
      throw error;
    } finally {
      setIsExporting(false);
    }
  }, [dispatch, queryParams, triggerExportList]);

  return (
    <>
      <PersistentServerSideDataGrid
        bulkActionFooter={isProductLoadingManage ? CustomActionFooter : null}
        checkboxSelection={isProductLoadingManage}
        columns={columns}
        defaultPageSize={paginationModel.pageSize}
        disableColumnMenu
        disableColumnReorder={isExporting}
        disabledExport={totalAuthorizations === 0}
        disableRowSelectionOnClick
        getRowId={R.prop('productAuthorizationId')}
        hideExportButton={!supplierExportFlag}
        id="supplier-authorizations-grid"
        initialState={{
          aggregation: {
            model: volumeAggregationModel,
          },
          pinnedColumns: {
            right: ['action'],
          },
        }}
        isExporting={isExporting}
        isProcessing={isProcessing}
        keepNonExistentRowsSelected
        loading={supplierAuthorizationsPending || isProcessing || isExporting}
        onExportAllRows={handleExportAllRows}
        onPaginationChange={model => {
          setPaginationModel(model);
        }}
        onResetView={() => setRowSelectionModel([])}
        onRowSelectionModelChange={
          isProductLoadingManage && !isExporting ? handleRowSelectionModelChange : undefined
        }
        onSortChange={handleSortModelChange}
        paginationStatus={authorizationCountLabel}
        rows={currentRows}
        rowSelectionModel={isProductLoadingManage ? currentPageSelection : undefined}
        totalRows={totalAuthorizations}
      />
    </>
  );
};

SupplierGridView.propTypes = {
  appliedFilters: PropTypes.shape({}),
  onExportingChange: PropTypes.func,
  supplierOptions: PropTypes.shape([]),
};
SupplierGridView.defaultProps = {
  appliedFilters: {},
  onExportingChange: null,
  supplierOptions: [],
};

export default SupplierGridView;
